import { Call } from '../model/call';
import { Message } from '../model/message';

export type AlertLogAttemptKind = 'call' | 'message';

export type AlertLogAttemptField = {
  label: string;
  value: string;
};

export type AlertLogAttempt = {
  kind: AlertLogAttemptKind;
  at: number;
  fields: AlertLogAttemptField[];
  body?: string;
  received?: boolean;
};

export function attemptTimestamp(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  const parsed = new Date(value as string | number | Date).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
}

export function callAttemptFields(call: Call): AlertLogAttemptField[] {
  const fields: AlertLogAttemptField[] = [
    { label: 'status', value: call.status?.trim() || 'unknown' },
  ];
  if (call.answeredBy?.trim()) {
    fields.push({ label: 'answeredBy', value: call.answeredBy.trim() });
  }
  if (call.duration != null) {
    fields.push({ label: 'duration', value: String(call.duration) });
  }
  if (call.sid?.trim()) {
    fields.push({ label: 'sid', value: call.sid.trim() });
  }
  return fields;
}

export function messageAttemptFields(message: Message): AlertLogAttemptField[] {
  const fields: AlertLogAttemptField[] = [
    { label: 'status', value: message.status?.trim() || 'unknown' },
  ];
  if (message.sid?.trim()) {
    fields.push({ label: 'sid', value: message.sid.trim() });
  }
  return fields;
}

export function attemptRowLabel(attempt: AlertLogAttempt): string {
  if (attempt.kind === 'call') {
    return 'Call:';
  }
  return attempt.received ? 'Message Received:' : 'Message:';
}

/** Oldest first — first attempt at the top. */
export function sequenceAttempts(sequence: {
  callLog?: Call[];
  messageLog?: Message[];
}): AlertLogAttempt[] {
  const calls = (sequence.callLog ?? []).map((call) => ({
    kind: 'call' as const,
    at: attemptTimestamp(call.created),
    fields: callAttemptFields(call),
  }));
  const messages = (sequence.messageLog ?? []).map((message) => ({
    kind: 'message' as const,
    at: attemptTimestamp(message.dateSent),
    fields: messageAttemptFields(message),
    body: message.body?.trim() ? message.body : undefined,
    received: message.status?.trim().toLowerCase() === 'received',
  }));

  return [...calls, ...messages].sort((a, b) => a.at - b.at);
}
